import { useAuth0 } from '@auth0/auth0-react';
import { useAppSelector } from '../redux/store';
import { selectUser } from '../redux/slices/menu';

/**
 * Unified authentication hook that combines Auth0 state with our custom auth loading state.
 *
 * This hook provides a single source of truth for authentication status, eliminating
 * the need to check multiple flags across the application.
 *
 * @returns {Object} Authentication state
 * @returns {boolean} isReady - True when auth state has been fully resolved (Auth0 + user sync complete)
 * @returns {boolean} isLoading - True while authentication is being resolved
 * @returns {boolean} isAuthenticated - True if user is authenticated (only trust this when isReady = true)
 * @returns {Object|undefined} user - The current user object from Redux, or undefined if not authenticated
 * @returns {Function} loginWithRedirect - Auth0 login function
 * @returns {Function} logout - Auth0 logout function
 * @returns {Function} getAccessTokenSilently - Auth0 token retrieval function
 *
 * @example
 * const { isReady, isAuthenticated, user } = useAuth();
 *
 * if (!isReady) return <Loading />;
 * if (!isAuthenticated) return <LoginPrompt />;
 * return <WelcomeMessage name={user.name} />;
 */
export const useAuth = () => {
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
    loginWithPopup,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  const authLoading = useAppSelector((state) => state.menu.authLoading);
  const user = useAppSelector(selectUser);

  const isLoading = authLoading || auth0Loading;
  const isReady = !isLoading;

  return {
    // Primary state flags
    isReady,
    isLoading,
    isAuthenticated,
    user,

    // Auth0 functions
    loginWithRedirect,
    loginWithPopup,
    logout,
    getAccessTokenSilently,
  };
};
